import { Button, Flex, Modal } from 'antd'
import { useEffect, useState } from 'react'
import { centsToDollarsDisplay } from '../../displayFormatters'
import VSpace from '../../common/VSpace'
import PaymentMethodsCard from '../SendInvoice/PaymentMethodsCard'
import PaymentMethodOnFileCard from '../SendInvoice/PaymentMethodOnFileCard'
import DueDateCard from '../SendInvoice/DueDateCard'
import { InvoicingSettings } from '../../../domain/invoicing/settings/invoicingSettings'
import { InvoiceSendOptions } from '../../../domain/invoicing/invoicingApi'
import { initialInvoiceSendOptions, isBulkSendSubmitDisabled } from './bulkSendOptionsModel'
import { getDefaultDueDate } from '../Creation/createInvoiceModel'

type Props = {
  open: boolean
  onClose: () => void
  onConfirm: (options: InvoiceSendOptions) => void
  selectedCount: number
  selectedTotalCents: number
  alreadySentCount: number
  invoicingSettings: InvoicingSettings
  isLoading: boolean
}

const BulkSendConfirmationModal = ({
  open, onClose, onConfirm,
  selectedCount, selectedTotalCents, alreadySentCount,
  invoicingSettings,
  isLoading,
}: Props) => {
  const [options, setOptions] = useState<InvoiceSendOptions>(() => initialInvoiceSendOptions(invoicingSettings))

  useEffect(() => {
    if (open) {
      setOptions(initialInvoiceSendOptions(invoicingSettings))
    }
  }, [open, invoicingSettings])

  const submitDisabled = isBulkSendSubmitDisabled(selectedCount, isLoading)

  const footer = (
    <div>
      <div style={{ borderTop: '1px solid #00000026', borderBottom: '1px solid #00000026', padding: '12px 0', fontSize: '16px' }}>
        <Flex justify="space-between" align="center">
          <strong>Estimated Total</strong>
          <div style={{ textAlign: 'right' }}>
            <div><strong>{centsToDollarsDisplay(selectedTotalCents)}</strong></div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total Invoices: {selectedCount}</div>
          </div>
        </Flex>
      </div>
      <Flex justify="flex-end" gap={8} style={{ paddingTop: 12 }}>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button type="primary" onClick={() => onConfirm(options)} loading={isLoading} disabled={submitDisabled}>
          Send
        </Button>
      </Flex>
    </div>
  )

  return (
    <Modal
      open={open}
      title="Send Invoices"
      onCancel={onClose}
      footer={footer}
      width={800}
      styles={{ body: { maxHeight: '60vh', display: 'flex', flexDirection: 'column' } }}
    >
      <div style={{ flexShrink: 0, paddingBottom: 16 }}>
        <p style={{ margin: 0 }}>
          You're about to send {selectedCount} invoice{selectedCount === 1 ? '' : 's'} totaling {centsToDollarsDisplay(selectedTotalCents)}.
          Each customer will receive their invoice via their primary email.
        </p>
        {alreadySentCount > 0 && (
          <p style={{ margin: '8px 0 0 0', color: '#8c8c8c' }}>
            {alreadySentCount} invoice{alreadySentCount === 1 ? ' has' : 's have'} already been sent and will be re-sent as a reminder.
          </p>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <VSpace>
          <PaymentMethodsCard options={options} onUpdate={setOptions} />
          <PaymentMethodOnFileCard options={options} onUpdate={setOptions} />
          <DueDateCard options={options} onUpdate={setOptions} defaultDueDate={getDefaultDueDate(invoicingSettings)} />
        </VSpace>
      </div>
    </Modal>
  )
}

export default BulkSendConfirmationModal
